import bbox from '@turf/bbox';
import centroid from '@turf/centroid';
import { ICasinoReport } from 'appRedux/models/casinoModels';
import { LngLatBoundsLike } from 'mapbox-gl';
import { Feature, Point } from 'geojson';
import { Properties } from '@turf/helpers';
import { GeoJson, GeoSlot } from '../IFloormap';
import { HeatBeanObjectKey } from 'config/config';
import { floormapHeatBeanRatings, floormapJackpotHeatBeanRatings } from 'config/HeatBeanRatings';
import { heatLegendAllInsight, hexToRgba } from 'common/common';
import { isInsightCardRow, matchesEgmDetailId } from 'services/insightCasino';
import { insightFeatureId, insightRowForFeature } from 'services/insightMapJoin';

export class mapboxHelper {
  static getColoredPin(number: number, color: string): string {
    return `<svg width="30" height="45" viewBox="0 0 30 45" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(-8, 0) scale(0.8)">
            <path d="M29.5 54.0833C28.9263 54.0833 28.4347 53.9194 28.025 53.5916C27.6152 53.2639 27.3079 52.8336 27.1031 52.301C26.3246 50.0066 25.3413 47.8555 24.1531 45.8479C23.0059 43.8402 21.3875 41.4843 19.2979 38.7802C17.2083 36.076 15.5079 33.4948 14.1968 31.0364C12.9267 28.5781 12.2916 25.6076 12.2916 22.125C12.2916 17.3312 13.951 13.275 17.2698 9.95621C20.6295 6.59649 24.7062 4.91663 29.5 4.91663C34.2937 4.91663 38.35 6.59649 41.6687 9.95621C45.0284 13.275 46.7083 17.3312 46.7083 22.125C46.7083 25.8534 45.9913 28.9673 44.5573 31.4666C43.1642 33.925 41.5458 36.3628 39.702 38.7802C37.4895 41.7302 35.8097 44.1885 34.6625 46.1552C33.5562 48.0809 32.6343 50.1295 31.8968 52.301C31.692 52.8746 31.3642 53.3253 30.9135 53.6531C30.5038 53.9399 30.0326 54.0833 29.5 54.0833ZM29.5 28.2708C31.2208 28.2708 32.6753 27.6767 33.8635 26.4885C35.0517 25.3003 35.6458 23.8458 35.6458 22.125C35.6458 20.4041 35.0517 18.9496 33.8635 17.7614C32.6753 16.5732 31.2208 15.9791 29.5 15.9791C27.7791 15.9791 26.3246 16.5732 25.1364 17.7614C23.9482 18.9496 23.3541 20.4041 23.3541 22.125C23.3541 23.8458 23.9482 25.3003 25.1364 26.4885C26.3246 27.6767 27.7791 28.2708 29.5 28.2708Z" fill="${color}"/>
            <rect x="17" y="10" width="24" height="25" rx="12" fill="${color}"/>
            <text x="29.5" y="26" text-anchor="middle" fill="white" font-size="16" font-family="Arial" font-weight="bold" dominant-baseline="middle">${number}</text>
          </g>
        </svg>`;
  }

  static getStarPin(number: number, color: string, size: number = 40) {
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 6.8l7.6 15.4 17 2.5-12.3 11.7 3 17.5L32 44.3 16.7 54.4l3-17.5L7.4 25.2l17-2.5L32 6.8z" fill="${color}" />
      </svg>
    `;

    const el = document.createElement('div');
    el.innerHTML = svg;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.display = 'inline-block';

    return el;
  }

  // Gold star badge overlaid on a numbered pin when its EGM is selected in the list.
  // White stroke keeps it legible against the (also gold) pin body.
  static getGoldStarBadge(size: number = 22) {
    const svg = `
      <svg width="${size}" height="${size}" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <path d="M32 6.8l7.6 15.4 17 2.5-12.3 11.7 3 17.5L32 44.3 16.7 54.4l3-17.5L7.4 25.2l17-2.5L32 6.8z"
              fill="#FFC83D" stroke="#FFFFFF" stroke-width="4" stroke-linejoin="round" />
      </svg>
    `;

    const el = document.createElement('div');
    el.innerHTML = svg;
    el.style.width = `${size}px`;
    el.style.height = `${size}px`;
    el.style.display = 'inline-block';
    // Don't let the star swallow taps meant for the pin underneath it.
    el.style.pointerEvents = 'none';

    return el;
  }

  static createNumberedPin(color: string, number: number, size: number = 40) {
    const svg = `<svg width="${size}" height="${
      size * 1.4
    }" viewBox="0 0 58 80" xmlns="http://www.w3.org/2000/svg">
      <!-- Pin body -->
      <path d="M29 77C28.45 77 27.95 76.85 27.55 76.55C27.15 76.25 26.85 75.85 26.65 75.35C25.9 72.9 24.95 70.65 23.85 68.65C22.8 66.65 21.2 64.3 19.15 61.65C17.1 59 15.45 56.45 14.15 54C12.9 51.55 12.3 48.6 12.3 45.1C12.3 40.3 13.95 36.25 17.27 32.95C20.63 29.6 24.7 27.9 29 27.9C33.3 27.9 37.35 29.6 40.73 32.95C44.05 36.25 45.7 40.3 45.7 45.1C45.7 48.9 44.98 52.05 43.55 54.55C42.16 57 40.55 59.45 38.7 61.85C36.5 64.8 34.82 67.25 33.65 69.25C32.55 71.15 31.63 73.15 30.9 75.35C30.7 75.85 30.35 76.25 29.95 76.55C29.55 76.85 29.05 77 29 77Z" fill="${color}"/>
      <!-- Circle head -->
      <circle cx="29" cy="45" r="14" fill="${color}" />
      <!-- Number text -->
      <text x="29" y="48" text-anchor="middle" dominant-baseline="middle" font-size="16" font-family="Arial" font-weight="bold" fill="white">${number}</text>
    </svg>
  `;

    const el = document.createElement('div');
    el.innerHTML = svg;
    el.style.width = `${size}px`;
    el.style.height = `${size * 1.4}px`;

    return el;
  }

  static createNumberedImagePin(
    imageUrl: string,
    number: number,
    width = 32,
    height = 47,
    pinBackground = '#14181F',
    pinNumberColor = '#FFF'
  ) {
    const badgeH = 24;
    const badgeW = 34;
    const gap = 3;
    const totalW = Math.max(width, badgeW);
    const totalH = badgeH + gap + height;
    const badgeX = (totalW - badgeW) / 2;
    const pinX = (totalW - width) / 2;

    const svg = `<svg
      width="${totalW}" height="${totalH}"
      viewBox="0 0 ${totalW} ${totalH}"
      xmlns="http://www.w3.org/2000/svg"
      xmlns:xlink="http://www.w3.org/1999/xlink"
    >
      <rect x="${badgeX}" y="0" width="${badgeW}" height="${badgeH}" rx="4.393" fill="${pinBackground}"/>
      <text
        x="${badgeX + badgeW / 2}" y="${badgeH / 2}"
        text-anchor="middle" dominant-baseline="middle"
        fill="${pinNumberColor}" font-size="14" font-weight="900"
        font-family="MODERNIZ, Arial, sans-serif"
      >${number}</text>
      <image
        href="${imageUrl}"
        x="${pinX}" y="${badgeH + gap}"
        width="${width}" height="${height}"
        preserveAspectRatio="xMidYMax meet"
      />
    </svg>`;

    const el = document.createElement('div');
    el.innerHTML = svg;
    el.style.width = `${totalW}px`;
    el.style.height = `${totalH}px`;
    el.style.pointerEvents = 'none';
    return el;
  }

  static createImagePin(imageUrl: string, width = 32, height = 47) {
    const svg = `<svg width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
      <image href="${imageUrl}" x="0" y="0" width="${width}" height="${height}" preserveAspectRatio="xMidYMax meet"/>
    </svg>`;

    const el = document.createElement('div');
    el.innerHTML = svg;
    el.style.width = `${width}px`;
    el.style.height = `${height}px`;
    return el;
  }

  static findIndividualSlot(
    slotData: ICasinoReport[],
    slotNumber: number
  ): ICasinoReport | undefined {
    // Insight: mnum (egmDetailId); legacy: slot_number.
    return slotData?.find((slot) => matchesEgmDetailId(slot, slotNumber));
  }

  static findPlayDataSlot(evt: any, slotData: ICasinoReport[]): ICasinoReport | undefined {
    const feature = evt?.features?.[0]?.properties;

    if (!feature || !slotData) {
      return undefined;
    }

    // Insight rows: match the clicked feature's id through the normalized join.
    const featureId = evt?.features?.[0]?.id ?? feature.kp_floor_plan_location_pk;
    const insightHit = insightRowForFeature(featureId);
    if (insightHit && slotData.includes(insightHit as ICasinoReport)) return insightHit as ICasinoReport;

    return slotData.find(
      (playSlot) =>
        !isInsightCardRow(playSlot) &&
        feature.area === playSlot.area &&
        feature.section === playSlot.section &&
        feature.location === playSlot.location
    );
  }

  static getMapBoundsAndCenter(geojson: GeoJson): {
    maxBounds: LngLatBoundsLike;
    mapCenter: Feature<Point, Properties>;
  } {
    const mapBounds = bbox(geojson);
    const mapCenter = centroid(geojson);

    const marginTop = 0.03;
    const marginBottom = 0.03;
    const marginLng = 0.01;

    const maxBounds: LngLatBoundsLike = [
      [mapBounds[0] - marginLng, mapBounds[1] - marginBottom],
      [mapBounds[2] + marginLng, mapBounds[3] + marginTop],
    ];

    return {
      maxBounds,
      mapCenter,
    };
  }

  /** Feature id for a row: insight rows use the normalized join, legacy the raw concat. */
  static geoSlotIdFor = (slotLocationIdLookup: Map<any, any>, slotDatum: any): any =>
    isInsightCardRow(slotDatum)
      ? insightFeatureId(slotDatum)
      : slotLocationIdLookup.get(slotDatum.area + slotDatum.section + slotDatum.location);

  static findTargetGeoSlot = (
    slotMapJsonWithIds: GeoJson | null | undefined,
    slotLocationIdLookup: Map<any, any>,
    slotDatum: any
  ): GeoSlot | undefined => {
    return (
      slotMapJsonWithIds &&
      slotMapJsonWithIds.features.find(
        (geoSlot: GeoSlot) =>
          geoSlot.id === mapboxHelper.geoSlotIdFor(slotLocationIdLookup, slotDatum)
      )
    );
  };

  static addMarkersToMap = (map: mapboxgl.Map, markers: mapboxgl.Marker[]): void => {
    for (let i = markers.length - 1; i >= 0; i--) {
      markers[i].addTo(map);
    }
  };
  static setPreferenceHeatMapColor = (heatFilter: number[], map: mapboxgl.Map, isJackpot = false) => {
    const ratings = isJackpot ? floormapJackpotHeatBeanRatings : floormapHeatBeanRatings;
    const newHeatFilter = [];

    for (const i of heatLegendAllInsight) {
      const isIndexInHeatFilter = heatFilter.length > 0 && heatFilter.includes(i);
      const colorToUse = isIndexInHeatFilter
        ? ratings[i as HeatBeanObjectKey].color
        : hexToRgba(ratings[i as HeatBeanObjectKey].color, 0.2);
      newHeatFilter.push(['match', ['feature-state', 'heat'], [i], true, false]);
      newHeatFilter.push(colorToUse);
    }
    const defaultColor = ratings[0].color;
    if (map) {
      map.setPaintProperty('slotLayer', 'fill-color', ['case', ...newHeatFilter, defaultColor]);
    }
  };
}
